import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import { Close, Delete } from '@mui/icons-material';
import {
  Dialog,
  DialogActions,
  DialogContent,
  LinearProgress,
  Select,
  MenuItem,
} from '@mui/material';
import React from 'react';
import { formStyles } from '../../util/styles';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import TextField from '@mui/material/TextField';
import Grid from '@mui/material/Grid';
import { useAlert } from '../../util/alertDialogContext';
import {
  listTemplateShares,
  addTemplateShare,
  updateTemplateShare,
  removeTemplateShare,
} from '../../db/sync/templateApi';

// Directly mirrors pages/CaseList/ShareModal.jsx, against the template
// share endpoints (GET/POST/PUT/DELETE /api/share/template/:templateId[/:shareId])
// instead of .../share/case/... Templates additionally expose an
// accessLevel choice ('view'|'edit') in the share list and add form.
const ShareModal = ({ open, onClose, templateId, templateName }) => {
  const classes = formStyles();

  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [shares, setShares] = React.useState([]);

  const [email, setEmail] = React.useState('');
  const [accessLevel, setAccessLevel] = React.useState('view');

  const { alert } = useAlert();

  React.useEffect(() => {
    setEmail('');
    setAccessLevel('view');
    setError(null);
    setShares([]);
    if (open && templateId) {
      getShares(templateId);
    } else {
      setLoading(false);
    }
  }, [templateId, open]);

  const getShares = async (id) => {
    setLoading(true);
    try {
      const shareList = await listTemplateShares(id);
      setShares(shareList || []);
    } catch (error) {
      console.error('Failed to get shares for template: ' + id, error);
      setError(error);
    }
    setLoading(false);
  };

  const handleClose = () => {
    onClose();
  };

  const handleShare = async () => {
    if (!email || email.indexOf('@') === -1) {
      setError(new Error('You must provide a valid email address.'));
      return;
    }

    try {
      setError(null);
      setLoading(true);

      const newShare = await addTemplateShare(templateId, { email, accessLevel });
      if (newShare) {
        setShares([...shares, newShare]);
        setEmail('');
        setAccessLevel('view');
      }
    } catch (error) {
      const message = error?.response?.data?.error;
      setError(message ? new Error(message) : error);
    }
    setLoading(false);
  };

  const handleAccessLevelChange = async (shareId, newAccessLevel) => {
    try {
      setError(null);
      await updateTemplateShare(templateId, shareId, { accessLevel: newAccessLevel });
      setShares(shares.map((s) => (s._id === shareId ? { ...s, accessLevel: newAccessLevel } : s)));
    } catch (error) {
      const message = error?.response?.data?.error;
      setError(message ? new Error(message) : error);
    }
  };

  const removeShare = async (shareId) => {
    try {
      setError(null);
      setLoading(true);
      await removeTemplateShare(templateId, shareId);
      setShares(shares.filter((s) => s._id !== shareId));
    } catch (error) {
      const message = error?.response?.data?.error;
      setError(message ? new Error(message) : error);
    }
    setLoading(false);
  };

  let progressStyles = { visibility: 'hidden' };
  if (loading) {
    progressStyles.visibility = 'visible';
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        Share {templateName ? `"${templateName}"` : ''}
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{ position: 'absolute', right: 8, top: 8, color: 'grey.500' }}
          size="large"
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {error !== null && (
          <Alert severity="error" onClose={() => setError(null)} key="share-list-error">
            {error.toString().replace('Error: ', '')}
          </Alert>
        )}
        <LinearProgress variant="indeterminate" color="primary" style={progressStyles} />

        {shares && shares.length > 0 && (
          <React.Fragment>
            <Typography variant="body2" color="textSecondary">
              Shared with {shares.length} {shares.length === 1 ? 'person' : 'people'}
            </Typography>
            <ul key="share-list" style={{ listStyle: 'none', paddingLeft: '0' }}>
              {shares.map((share) => (
                <li key={`share-${share._id}`}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid size={6}>{share.email}</Grid>
                    <Grid size={4}>
                      <Select
                        size="small"
                        fullWidth
                        value={share.accessLevel || 'view'}
                        onChange={(e) => handleAccessLevelChange(share._id, e.target.value)}
                      >
                        <MenuItem value="view">View</MenuItem>
                        <MenuItem value="edit">Edit</MenuItem>
                      </Select>
                    </Grid>
                    <Grid size={2} style={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.preventDefault();
                          alert({
                            msg: `Are you sure you want to stop sharing this template with '${share.email}'?`,
                            onSubmit: () => removeShare(share._id),
                            variant: 'danger',
                          });
                        }}
                      >
                        <Delete />
                      </IconButton>
                    </Grid>
                  </Grid>
                </li>
              ))}
            </ul>
          </React.Fragment>
        )}
        {!loading && shares.length === 0 && (
          <Typography variant="body2" color="textSecondary">
            This template has not been shared with anyone yet.
          </Typography>
        )}

        <hr style={{ marginTop: '15px', marginBottom: '15px' }} />

        <Grid container spacing={2} alignItems="center">
          <Grid size={12}>
            <Typography variant="body1">Add a user to share the template with:</Typography>
          </Grid>
          <Grid size={7}>
            <TextField
              size="small"
              variant="outlined"
              fullWidth
              id="email"
              name="email"
              label="Email address"
              autoFocus
              value={email}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleShare();
                }
              }}
              onChange={(event) => setEmail(event.target.value)}
            />
          </Grid>
          <Grid size={5}>
            <Select
              size="small"
              fullWidth
              value={accessLevel}
              onChange={(e) => setAccessLevel(e.target.value)}
            >
              <MenuItem value="view">Can view</MenuItem>
              <MenuItem value="edit">Can edit</MenuItem>
            </Select>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <Button onClick={handleShare} color="primary" variant="contained">Add</Button>
        <Button onClick={handleClose} color="primary">Cancel</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ShareModal;
